/**
 * ClipprContext.tsx - ClipprContext - React context for clipboard management
 * 
 * CHANGE HISTORY:
 * - 251021_0543 [d4517d3] danielbeer: feat: apply performance optimizations to all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { createContext, useContext, useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { getClipr } from './Clippr';

const ClipprContext = createContext(null);

export const ClipprProvider = ({ children }) => {
  const clipprRef = useRef(getClipr({ actr }));
  const [, forceUpdate] = useState({});
  
  const clippr = clipprRef.current;
  
  // Subscribe to changes
  useEffect(() => {
    const unsubscribe = clippr.subscribe(() => {
      forceUpdate({});
    });
    
    return unsubscribe;
  }, [clippr]);
  
  const value = useMemo(() => ({
  
    clippr,
    
    // Core methods
    copy: useCallback((data, options) => clippr.copy(data, options), [clippr]),
    cut: useCallback((data, options) => clippr.cut(data, options), [clippr]),
    paste: useCallback((options) => clippr.paste(options), [clippr]),
    peek: useCallback((slot) => clippr.peek(slot), [clippr]),
    hasData: useCallback((slot) => clippr.hasData(slot), [clippr]),
    clear: useCallback((slot) => clippr.clear(slot), [clippr]),
    
    // History methods
    getHistory: useCallback(() => clippr.getHistory(), [clippr]),
    pasteFromHistory: useCallback((index) => clippr.pasteFromHistory(index), [clippr]),
    
    // Slot methods
    getSlots: useCallback(() => clippr.getSlots(), [clippr])
  }), [clippr, copy, cut, paste, peek, hasData, clear, getHistory, pasteFromHistory, getSlots]);
  
  return (
    <ClipprContext.Provider value={value}>
      {children}
    </ClipprContext.Provider>
  );
};

export const useClipr = () => {
  const context = useContext(ClipprContext);
  
  if (!context) {
    throw new Error('useClipr must be used within ClipprProvider');
  }
  
  return context;
};

/**
 * Hook to handle copy/paste for a component
 */
export const useClipboard = (options = {}) => {
  const { copy, paste, hasData } = useClipr();
  const { onPaste } = options;
  
  const handleCopy = useCallback((data) => {
    return copy(data, options);
  }, [copy, options]);
  
  const handlePaste = useCallback(() => {
    const data = paste(options);
    if (data && onPaste) {
      onPaste(data);
    }
    return data;
  }, [paste, onPaste, options]);
  
  return {
    copy: handleCopy,
    paste: handlePaste,
    hasData: hasData()
  };
};

export default ClipprContext;
